import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import './Layout.css';

interface LayoutProps {
  children: React.ReactNode;
  onLogout: () => void;
  user?: { email: string; firstName: string; lastName: string } | null;
}

const NAV_ITEMS = [
  { id: 'knowledge-graphs', label: 'Knowledge Graphs', path: '/kg',         icon: <KGIcon /> },
  { id: 'instances',        label: 'Instances',        path: '/instances',  icon: <InstanceIcon /> },
  { id: 'audit-log',        label: 'Audit Log',        path: '/audit-log',  icon: <AuditIcon /> },
  { id: 'analytics',        label: 'Analytics',        path: '/analytics',  icon: <AnalyticsIcon /> },
];

function KGIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5v6c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
      <path d="M3 11v6c0 1.66 4.03 3 9 3s9-1.34 9-3v-6" />
    </svg>
  );
}
function InstanceIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  );
}
function AnalyticsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 16.5V9M8 16.5V4M13 16.5v-5M18 16.5V7" />
    </svg>
  );
}
function AuditIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

function KGMark() {
  // KG Manager on-dark mark: knowledge graph propagating downward into versions
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="28" height="28" role="img" aria-label="KG Manager">
      <g fill="none" stroke="#C79BFA" strokeWidth="2" strokeLinecap="round">
        <path d="M13.6 11.5 L8.9 18.9" />
        <path d="M18.4 11.5 L23.1 18.9" />
        <path d="M9.92 24.11 L14.05 27.09" />
        <path d="M23.08 24.11 L17.95 27.09" />
      </g>
      <rect x="11.5" y="3" width="9" height="9" rx="2.8" fill="rgba(255,255,255,0.42)" />
      <circle cx="7" cy="22" r="3.6" fill="rgba(255,255,255,0.42)" />
      <circle cx="25" cy="22" r="3.6" fill="rgba(255,255,255,0.42)" />
      <circle cx="16" cy="28.5" r="2.4" fill="#B478F5" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}
function ChevronRightIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
function SearchIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

interface BreadcrumbItem {
  label: string;
  path: string | null;
}

function getBreadcrumbs(pathname: string): BreadcrumbItem[] {
  // Match /kg/:kgId/version/:versionId
  const versionMatch = pathname.match(/^\/kg\/([^/]+)\/version\/([^/]+)/);
  if (versionMatch) {
    const kgId = versionMatch[1];
    const versionId = versionMatch[2];
    return [
      { label: 'Knowledge Graphs', path: '/kg' },
      { label: kgId, path: `/kg/${kgId}` },
      { label: `v${versionId}`, path: null },
    ];
  }
  // Match /kg/:kgId
  const kgMatch = pathname.match(/^\/kg\/([^/]+)$/);
  if (kgMatch) {
    const kgId = kgMatch[1];
    return [
      { label: 'Knowledge Graphs', path: '/kg' },
      { label: kgId, path: null },
    ];
  }
  // /kg or /instances or /analytics
  if (pathname.startsWith('/kg')) return [{ label: 'Knowledge Graphs', path: null }];
  if (pathname.startsWith('/instances')) return [{ label: 'Instances', path: null }];
  if (pathname.startsWith('/analytics')) return [{ label: 'Analytics', path: null }];
  if (pathname.startsWith('/audit-log')) return [{ label: 'Audit Log', path: null }];
  return [{ label: 'Knowledge Graphs', path: null }];
}

const Layout: React.FC<LayoutProps> = ({ children, onLogout, user }) => {
  const env = apiService.getEnv();
  const location = useLocation();
  const navigate = useNavigate();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState('');
  // Tick to force re-read of sessionStorage when VersionDetails writes the badge
  const [badgeTick, setBadgeTick] = useState(0);
  const paletteInputRef = useRef<HTMLInputElement>(null);
  const [orgId, setOrgId] = useState(() => sessionStorage.getItem('polly_org_id') ?? '');

  // Re-read from sessionStorage whenever KGDetails refreshes it
  useEffect(() => {
    const handler = () => setOrgId(sessionStorage.getItem('polly_org_id') ?? '');
    window.addEventListener('pollyOrgIdUpdated', handler);
    return () => window.removeEventListener('pollyOrgIdUpdated', handler);
  }, []);

  useEffect(() => {
    // If already in sessionStorage (from a previous visit), nothing to do
    if (sessionStorage.getItem('polly_org_id')) return;
    // Fast path: already cached from a previous KG page visit
    const cached = sessionStorage.getItem('polly_org_id');
    if (cached) { setOrgId(cached); return; }

    // Slow path: fetch from the KG list — org_id lives in item.attributes.org_id
    (async () => {
      try {
        const kgRes: any = await apiService.listKGs();
        const items: any[] = kgRes?.data ?? [];
        for (const item of items) {
          const id = item?.org_id ?? item?.attributes?.org_id ?? '';
          if (id) {
            sessionStorage.setItem('polly_org_id', String(id));
            setOrgId(String(id));
            return;
          }
        }
        // Last resort: fetch a version from the first KG
        if (items.length > 0) {
          const kgId = items[0]?.kg_id ?? items[0]?.id ?? items[0]?.attributes?.kg_id ?? '';
          if (kgId) {
            const verRes: any = await apiService.listVersions(kgId);
            const verItems: any[] = verRes?.data ?? [];
            for (const v of verItems) {
              const id = v?.org_id ?? v?.attributes?.org_id ?? '';
              if (id) {
                sessionStorage.setItem('polly_org_id', String(id));
                setOrgId(String(id));
                return;
              }
            }
          }
        }
      } catch { /* ignore */ }
    })();
  }, []);

  // Auto-collapse sidebar on narrow screens
  useEffect(() => {
    const check = () => setSidebarCollapsed(window.innerWidth < 900);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Re-read sessionStorage badge whenever VersionDetails updates it
  useEffect(() => {
    const handler = () => setBadgeTick(t => t + 1);
    window.addEventListener('versionBadgeUpdated', handler);
    return () => window.removeEventListener('versionBadgeUpdated', handler);
  }, []);

  // ⌘K / Ctrl+K → open command palette; Esc → close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen(v => !v);
        setPaletteQuery('');
      }
      if (e.key === 'Escape') setPaletteOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Lock body scroll when palette open; focus input
  useEffect(() => {
    if (paletteOpen) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => paletteInputRef.current?.focus(), 50);
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [paletteOpen]);

  const PALETTE_LINKS = [
    { label: 'Knowledge Graphs', path: '/kg',         hint: 'Go to KG list' },
    { label: 'Instances',        path: '/instances',  hint: 'Go to fleet' },
    { label: 'Analytics',        path: '/analytics',  hint: 'View analytics dashboard' },
  ];
  const paletteResults = PALETTE_LINKS.filter(l =>
    !paletteQuery || l.label.toLowerCase().includes(paletteQuery.toLowerCase())
  );

  const breadcrumbs = getBreadcrumbs(location.pathname);
  const envLabel = env ? env.toUpperCase() : 'KG MANAGER';
  const envInitial = envLabel[0] ?? 'K';

  // Version badge: shown only on /kg/:kgId/version/:versionId routes
  // versionId comes from the URL (always correct); published status from sessionStorage
  // keyed by versionId so a stale entry from a different version is never shown.
  // badgeTick forces a re-read when VersionDetails dispatches 'versionBadgeUpdated'.
  const versionMatch = location.pathname.match(/^\/kg\/[^/]+\/version\/([^/]+)/);
  const urlVersionId = versionMatch ? versionMatch[1] : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const versionBadge = React.useMemo(() => {
    if (!urlVersionId) return null;
    try {
      const stored = JSON.parse(sessionStorage.getItem('versionBadge') || 'null');
      const published = stored?.versionId === urlVersionId ? (stored.published ?? false) : null;
      return { versionId: urlVersionId, published };
    } catch { return { versionId: urlVersionId, published: null }; }
  }, [urlVersionId, badgeTick]); // re-compute when URL changes OR badge is written

  const getActiveNavId = () => {
    if (location.pathname.startsWith('/instances')) return 'instances';
    if (location.pathname.startsWith('/audit-log')) return 'audit-log';
    if (location.pathname.startsWith('/analytics')) return 'analytics';
    if (location.pathname.startsWith('/kg')) return 'knowledge-graphs';
    return 'knowledge-graphs';
  };
  const activeNavId = getActiveNavId();

  return (
    <div className={`layout${sidebarCollapsed ? ' layout--collapsed' : ''}`}>

      {/* ── Sidebar ─────────────────────────────────────────────────────────── */}
      <aside className="sidebar">

        {/* Brand */}
        <div className="sidebar-brand">
          <div className="sidebar-brand-mark"><KGMark /></div>
          <span className="sidebar-brand-text">KG<span className="sidebar-brand-text-muted"> Manager</span></span>
        </div>

        {/* Workspace switcher */}
        <div className="sidebar-workspace">
          <div className="sidebar-workspace-dot" />
          <span className="sidebar-workspace-initial">{envInitial}</span>
          <div className="sidebar-workspace-info">
            <span className="sidebar-workspace-name">{envLabel}</span>
            {orgId && <span className="sidebar-workspace-org">Org ID: {orgId}</span>}
          </div>
        </div>

        {/* Nav */}
        <nav className="sidebar-nav">
          {NAV_ITEMS.map((item) => {
            const isActive = item.id === activeNavId;
            const isDisabled = item.path === null;
            return (
              <button
                key={item.id}
                className={`sidebar-nav-item${isActive ? ' sidebar-nav-item--active' : ''}${isDisabled ? ' sidebar-nav-item--disabled' : ''}`}
                onClick={() => { if (item.path) navigate(item.path); }}
                title={sidebarCollapsed ? item.label : undefined}
              >
                <span className="sidebar-nav-icon">{item.icon}</span>
                <span className="sidebar-nav-label">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Collapse toggle */}
        <button
          className="sidebar-collapse-btn"
          onClick={() => setSidebarCollapsed(v => !v)}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!sidebarCollapsed}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </button>

        {/* User / Logout section */}
        <div className="sidebar-user">
          {user?.email && (
            <div className="sidebar-user-info">
              <div className="sidebar-user-avatar">
                {(user.firstName?.[0] ?? user.email[0]).toUpperCase()}
              </div>
              <div className="sidebar-user-details">
                {(user.firstName || user.lastName) && (
                  <span className="sidebar-user-name">
                    {[user.firstName, user.lastName].filter(Boolean).join(' ')}
                  </span>
                )}
                <span className="sidebar-user-email">{user.email}</span>
              </div>
            </div>
          )}
          <button className="sidebar-logout-btn" onClick={onLogout} title="Log out">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sidebar-logout-icon">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            <span className="sidebar-logout-label">Log out</span>
          </button>
        </div>
      </aside>

      {/* ── Right column ─────────────────────────────────────────────────────── */}
      <div className="layout-right">

        {/* Top bar */}
        <header className="topbar">
          <div className="topbar-left">
            <nav className="breadcrumbs" aria-label="Breadcrumb">
              {breadcrumbs.map((crumb, idx) => (
                <React.Fragment key={idx}>
                  {idx > 0 && <span className="breadcrumb-sep" aria-hidden="true">›</span>}
                  {crumb.path ? (
                    <button
                      className="breadcrumb-item"
                      onClick={() => navigate(crumb.path!)}
                    >
                      {crumb.label}
                    </button>
                  ) : (
                    <span className="breadcrumb-current" aria-current="page">{crumb.label}</span>
                  )}
                </React.Fragment>
              ))}
            </nav>
          </div>
          <div className="topbar-right">
            {versionBadge && (
              <div className="topbar-version-badge">
                <span className="topbar-version-id">v{versionBadge.versionId}</span>
                {versionBadge.published !== null && (
                  <span className={`topbar-version-status topbar-version-status--${versionBadge.published ? 'published' : 'draft'}`}>
                    {versionBadge.published ? 'PUBLISHED' : 'DRAFT'}
                  </span>
                )}
              </div>
            )}
          </div>
        </header>

        {/* Main content */}
        <main className="main-content">{children}</main>
      </div>

      {/* ── Command palette ─────────────────────────────────────────────────── */}
      {paletteOpen && (
        <div className="palette-overlay" onClick={() => setPaletteOpen(false)} role="dialog" aria-modal="true" aria-label="Command palette">
          <div className="palette-dialog" onClick={e => e.stopPropagation()}>
            <div className="palette-search-row">
              <SearchIcon />
              <input
                ref={paletteInputRef}
                className="palette-input"
                placeholder="Search KGs, instances, run actions…"
                value={paletteQuery}
                onChange={e => setPaletteQuery(e.target.value)}
              />
              <span className="palette-esc-hint">ESC</span>
            </div>
            <div className="palette-results">
              {paletteResults.length === 0 ? (
                <div className="palette-empty">No results for "{paletteQuery}"</div>
              ) : (
                paletteResults.map(r => (
                  <button
                    key={r.path}
                    className="palette-item"
                    onClick={() => { navigate(r.path); setPaletteOpen(false); }}
                  >
                    <span className="palette-item-label">{r.label}</span>
                    <span className="palette-item-hint">{r.hint}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
